import { chain, sortBy } from 'lodash';
import { useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useStartBotCRUDActions } from '@mm/state/actions/StartBotCrudActions';
import { useAdConfigCRUDActions } from '@mm/state/actions/AdConfigCRUDActions';
import { useMessengerAdCRUDActions } from '@mm/state/actions/MessengerAdCRUDActions';
import { useSponsoredMessageCRUDActions } from '@mm/state/actions/SponsoredMessageCRUDActions';
import {
  CampaignStartBotResponse,
  StartBotFromCampaign,
} from '@mm/state/controllers/StartBotController';
import { CampaignScheduleType } from '@mm/state/model/CampaignsModel';
import { LinkTypes } from '@mm/state/model/PromoterModel';
import { selectWelcomePages } from '@mm/state/selectors/pageSelectors';
import {
  selectCommentGuards,
  selectCustomerChats,
  selectShareLinks,
  selectFacebookOptIns,
  selectOptinPopups,
  selectInstagramCommentGuards,
  selectInstagramWelcomer,
  selectInstagramStoryMentions,
  selectMSKeywords,
} from '@mm/state/selectors/promoterSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';

const enum OptionGroup {
  drip = 0,
  blaster = 1,
  welcomer = 2,
  commentGuard = 3,
  customerChat = 4,
  shareLink = 5,
  facebookOptIns = 6,
  syncedAds = 7,
  newAds = 8,
  sm = 9,
  smsKeywords = 10,
  leadOptIns = 11,
  instagramWelcomer = 12,
  instagramCG = 13,
  instagramStoryMention = 14,
}

type Options = {
  id: any;
  title: string;
  suggestions: any;
}[];

const enum ActionType {
  campaign = 'campaign',
  promoter = 'promoter',
}

type Action = {
  type: ActionType;
  payload?: CampaignStartBotResponse;
};

const TITLES = {
  [OptionGroup.blaster]: 'Blast campaigns',
  [OptionGroup.drip]: 'Drip campaigns',
};

function mapCampaignStartBotsToOptions(
  options: Options,
  campaignStartBots?: CampaignStartBotResponse,
): Options {
  if (!campaignStartBots) return options;

  const { startBots } = campaignStartBots;

  const isBlasterType = (type: CampaignScheduleType | string) =>
    type === 'none' || type === 'global' || type === 'timezone';

  const mapBotsNameAndOrder = (arr: StartBotFromCampaign[]) => {
    const sorted = sortBy(arr, 'sortOrder');
    return sorted.map(({ name, sortOrder, destinationPageId, ...rest }) => ({
      ...rest,
      destinationPageId,
      id: destinationPageId, // prevent key duplicates in renders, see CampaignStartBotResponse
      name: `${name} (Dialogue ${sortOrder + 1})`,
      sortOrder,
    }));
  };

  const flatStartBotCollection = (arr: StartBotFromCampaign[]) =>
    // some campaigns might have more than one startbot
    // update data model according to view reqs
    arr.length > 1 ? mapBotsNameAndOrder(arr) : arr;

  const groupByCampaignType = ({ scheduleType }: StartBotFromCampaign) =>
    isBlasterType(scheduleType) ? OptionGroup.blaster : OptionGroup.drip;

  const mapGroupTitleAndId = (
    bots: StartBotFromCampaign[],
    type: OptionGroup.blaster | OptionGroup.drip,
  ) => ({
    id: type,
    title:
      type === OptionGroup.blaster
        ? TITLES[OptionGroup.blaster]
        : TITLES[OptionGroup.drip],
    suggestions: bots,
  });

  const groupedByType = chain(startBots)
    .flatMap(flatStartBotCollection)
    .groupBy(groupByCampaignType)
    .map((el, type) => mapGroupTitleAndId(el, Number(type)))
    .toArray()
    .value();

  return [...options, ...groupedByType];
}

const optionsReducer = (options: Options, action: Action) => {
  switch (action.type) {
    case ActionType.campaign:
      return mapCampaignStartBotsToOptions(options, action.payload);
    default:
      return options;
  }
};

// fetches all startbots options (both from promoters & campaigns)
const useCampaignStartBotOptions = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [options, dispatch] = useReducer(optionsReducer, []);
  const promoterCRUDActions = usePromoterCRUDActions();
  const startBotCRUDActions = useStartBotCRUDActions();

  const id = useSelector(activeChatbotIdSelector);
  const activeFbPage = useSelector(activeFacebookPageSelector);

  useEffect(() => {
    if (id) {
      const fetchOptions = async () => {
        setIsLoading(true);

        const promoterTypes: LinkTypes[] = [
          'customer_chat',
          'fb_comment_guard',
          'checkbox',
          'send_to_messenger',
          'link',
        ];
        if (activeFbPage?.instagramActivated) {
          promoterTypes.push(
            'instagram_welcomer',
            'instagram_story_mention',
            'instagram_comment_guard',
          );
        }
        if (activeFbPage?.smsActivated) {
          promoterTypes.push(
            'optin',
            // 'sms_keywords',
          );
        }

        promoterTypes.map(kind => promoterCRUDActions.retrieveAll(id, kind));

        const campaignStartBots =
          await startBotCRUDActions.retrieveCampaignsStartBots(id);

        dispatch({ type: ActionType.campaign, payload: campaignStartBots });

        setIsLoading(false);
      };

      fetchOptions();
    }
  }, [id]);

  return {
    options,
    isLoading,
  };
};

/*
  Maps all star bots to proper model
  TODO replace selectors with fetch after BE startbot promoter kind will be present in response
 */
const usePageStartBotOptions = () => {
  const welcomePages = useSelector(selectWelcomePages);
  const commentGuards = useSelector(selectCommentGuards);
  const customerChats = useSelector(selectCustomerChats);
  const shareLinks = useSelector(selectShareLinks);
  const facebookOptIns = useSelector(selectFacebookOptIns);
  const leadOptIns = useSelector(selectOptinPopups);
  const instagramWelcomer = useSelector(selectInstagramWelcomer);
  const instagramCG = useSelector(selectInstagramCommentGuards);
  const instagramStoryMention = useSelector(selectInstagramStoryMentions);
  const smsKeywords = useSelector(selectMSKeywords);

  const options = [
    {
      id: OptionGroup.welcomer,
      title: 'Welcomer',
      suggestions: welcomePages,
    },
    {
      id: OptionGroup.commentGuard,
      title: 'Comment Guard',
      suggestions: commentGuards,
    },
    {
      id: OptionGroup.customerChat,
      title: 'Customer Chat',
      suggestions: customerChats,
    },
    {
      id: OptionGroup.shareLink,
      title: 'Share Link',
      suggestions: shareLinks,
    },
    {
      id: OptionGroup.facebookOptIns,
      title: 'Facebook Opt-in',
      suggestions: facebookOptIns,
    },
    // BE is not implemented yet
    // {
    //   id: OptionGroup.smsKeywords,
    //   title: 'SMS Keywords',
    //   suggestions: smsKeywords,
    // },
    {
      id: OptionGroup.leadOptIns,
      title: 'SMS Opt-In Popups',
      suggestions: leadOptIns,
    },
    {
      id: OptionGroup.instagramWelcomer,
      title: 'Instagram DM Responder',
      suggestions: instagramWelcomer,
    },
    {
      id: OptionGroup.instagramCG,
      title: 'Instagram Commet Guard',
      suggestions: instagramCG,
    },
    {
      id: OptionGroup.instagramStoryMention,
      title: 'Instagram Story Mention',
      suggestions: instagramStoryMention,
    },
  ];

  return {
    options: options.filter(opts => opts.suggestions.length),
  };
};

export function useStartBotAdsOptions() {
  const [isLoading, setIsLoading] = useState(true);
  const id = useSelector(activeChatbotIdSelector);
  const adConfigCRUDActons = useAdConfigCRUDActions();
  const messengerAdCRUDActions = useMessengerAdCRUDActions();

  const adConfigs = useSelector(
    (state: StoreState) => state.adConfigs.entities,
  );
  const messengerAds = useSelector((state: StoreState) =>
    state.messengerAds.entities.filter(ma => !ma.legacy),
  );

  useEffect(() => {
    if (id) {
      const adConfigEntities = adConfigCRUDActons.retrieveAll(id);
      const messengerAdEntities = messengerAdCRUDActions.retrieveAll(id);

      (async () => {
        await Promise.all([adConfigEntities, messengerAdEntities]);
        setIsLoading(false);
      })();
    }
  }, [id]);

  return {
    isLoading,
    organizedOptions: [
      {
        id: OptionGroup.syncedAds,
        title: 'Synced Ads',
        suggestions: messengerAds,
      },
      {
        id: OptionGroup.newAds,
        title: 'Published Ads',
        suggestions: adConfigs,
      },
    ],
  };
}

export function useStartBotSMOptions() {
  const [isLoading, setIsLoading] = useState(true);
  const id = useSelector(activeChatbotIdSelector);
  const sponsoredCRUDActions = useSponsoredMessageCRUDActions();

  const sponsoredMessages = useSelector(
    (state: StoreState) => state.sponsoredMessages.entities,
  );

  useEffect(() => {
    if (id) {
      (async () => {
        await sponsoredCRUDActions.retrieveAll(id, { perPage: 999 });
        setIsLoading(false);
      })();
    }
  }, [id]);

  return {
    isLoading,
    options: sponsoredMessages.map(sm => ({
      ...sm,
      pageId: sm.textPageSelected ? sm.textPageId : sm.qqPageId,
    })),
  };
}

export function useStartBotOptions() {
  const { isLoading: campaignsIsLoading, options: campaignOptions } =
    useCampaignStartBotOptions();
  const { options: smOptions, isLoading: smIsLoading } = useStartBotSMOptions();
  const { organizedOptions: adsOptions, isLoading: adsIsLoading } =
    useStartBotAdsOptions();
  const { options } = usePageStartBotOptions();
  return {
    isLoading: adsIsLoading || campaignsIsLoading || smIsLoading,
    organizedOptions: [
      ...options,
      ...campaignOptions,
      ...adsOptions,
      {
        id: OptionGroup.sm,
        title: 'Sponsored Messages',
        suggestions: smOptions,
      },
    ],
  };
}
